interface ConditionInterface {
  [name: string]: string | MatchInterface;
}

interface MatchInterface {
  [op: string]: string;
}

interface FieldInterface {
  [key: string]: string | boolean | number;
}

/**
 * Returns true if all the conditions are matched within the given fields.
 * @param conditions - The array of conditions
 * @param fields - An object containing every available field on the page
 */
export function matchAll(conditions: ConditionInterface[], fields: FieldInterface): boolean {
  // We must ensure that the conditions are an array, if it's an object (default way that NSP sent conditions if there's only one) we send a log
  if (conditions?.constructor === Object) console.warn(`Conditions must be an array ${conditions}`);
  if (!conditions || !conditions.length) return true;

  return conditions.every((condition) =>
    Object.entries(condition).every(([name, value]) => matchOne(fields[name], value))
  );
}

/**
 * Performs a conditional match check against a field with the given test value.
 * This functionality is considered identical to that in Atlas::Extensions::Dato::Matcher.match?
 *
 * @param {string} fieldValue - The form or page field value
 * @param {string|MatchInterface} testValue - The value of our conditional test
 * @returns {boolean} True if the match succeeded
 */
export function matchOne(
  fieldValue: string | number | boolean,
  testValue: string | MatchInterface
): boolean {
  if (testValue && typeof testValue === 'object') {
    return Object.entries(testValue).every(([type, value]) =>
      matchAdvanced(fieldValue, type, value)
    );
  } else {
    return matchString(fieldValue, testValue as string);
  }
}

function matchString(fieldValue: string | number | boolean, testValue: string): boolean {
  // if test value is blank, test presence
  if (!testValue || !fieldValue) {
    return !!fieldValue;
  }

  // comma-split the field value to check any value
  return String(fieldValue)
    .toLowerCase()
    .split(/,\s*/)
    .some((value) => {
      // if test value matches /regex/, test as regex
      if (testValue.match(/^\/.+\/$/)) {
        return matchRegexp(value, testValue);
      }

      // test as comma-split string
      return testValue.toLowerCase().split(/,\s*/).includes(value);
    });
}

function matchRegexp(fieldValue: string | number | boolean, testValue: string): boolean {
  try {
    return new RegExp(testValue.match(/^\/(.+)\/$/)[1], 'i').test(String(fieldValue));
  } catch {
    return false;
  }
}

/**
 * Performs a conditional match check against the form field with the given test.
 * The type is expected to be one or more advanced check types.
 * Supported conditions are eq, neq, in, nin, contain, ncontain, presence, blank, regex.
 *
 * @param {string} fieldValue
 * @param {string} type
 * @param {string} testValue
 * @returns {boolean}
 */
function matchAdvanced(
  fieldValue: string | number | boolean,
  type: string,
  testValue: string
): boolean {
  const lowerFieldValue = fieldValue ? String(fieldValue).toLowerCase() : '';
  const lowerTestValue = testValue ? String(testValue).toLowerCase() : '';

  if (type[0] === 'n') {
    return !_matchAdvancedInternal(lowerFieldValue, type.slice(1), lowerTestValue);
  }

  return _matchAdvancedInternal(lowerFieldValue, type, lowerTestValue);
}

function _matchAdvancedInternal(value: string, type: string, testValue: string): boolean {
  try {
    switch (type) {
      case 'contains':
        return value.includes(testValue);
      case 'eq':
        return value === testValue;
      case 'in':
        return testValue.split(/,\s*/).includes(value);
      case 'presence':
        return !!value;
      case 'blank':
        return !value;
      case 'regex':
        return new RegExp(testValue).test(value);
      default:
        return true;
    }
  } catch {
    return false;
  }
}
